/** Writer flips on the confirm screen. Kinds stay prose, outline, or unsure. */

export const PROSE_KINDS = ['prose', 'outline', 'unsure'];

export function sectionKey(fileIndex, index) {
  return `${fileIndex}:${index}`;
}

export function initialKindOverrides(report) {
  const overrides = {};
  (report?.files || []).forEach((file) => {
    (file.sections || []).forEach((section) => {
      if (section?.role !== 'scene') return;
      overrides[sectionKey(file.file_index, section.index)] = section.kind || 'unsure';
    });
  });
  return overrides;
}

/**
 * Where chapter scenes would land.
 *
 * A started book is a story row. An idea-only Timeline card has no story,
 * so it is not a place for scenes and it is not in `stories`.
 * Series altitude passes no storyId. One story is that book: Book 1
 * when Book 2 is idea-only. Zero stories, or more than one story, is
 * no book. The no-book line shows and Add scenes stays off.
 */
export function proseUploadTarget({
  storyId = null,
  stories = [],
  lookupSettled = false,
} = {}) {
  if (storyId) {
    return { bookId: String(storyId), noBook: false, landsIn: 'the open book' };
  }
  if (!lookupSettled) {
    return { bookId: null, noBook: false, landsIn: 'still checking' };
  }
  const ids = [];
  (stories || []).forEach((row) => {
    const id = row?.id;
    if (id && !ids.includes(String(id))) ids.push(String(id));
  });
  if (ids.length === 1) {
    return { bookId: ids[0], noBook: false, landsIn: 'the only started book' };
  }
  return {
    bookId: null,
    noBook: true,
    landsIn: ids.length === 0 ? 'no started book' : 'more than one started book',
  };
}

export function proseKindsFromReport(report, overrides = {}) {
  const counts = { prose: 0, outline: 0, unsure: 0, chapters: 0, scenes: 0, split: false };
  const decisions = [];
  const rows = [];
  (report?.files || []).forEach((file) => {
    (file.sections || []).forEach((section) => {
      if (section?.role !== 'scene') return;
      const key = sectionKey(file.file_index, section.index);
      const kind = PROSE_KINDS.includes(overrides[key]) ? overrides[key] : (section.kind || 'unsure');
      if (counts[kind] != null) counts[kind] += 1;
      decisions.push({
        file_index: file.file_index,
        index: section.index,
        kind,
      });
      rows.push({
        kind,
        chapterName: section.chapter_name || section.heading || '',
        pieceCount: Number(section.piece_count) || 1,
      });
    });
  });
  const split = rows.some((row) => row.pieceCount > 1);
  if (split) {
    const names = [];
    rows.forEach((row) => {
      if (row.kind !== 'prose') return;
      if (row.chapterName && !names.includes(row.chapterName)) names.push(row.chapterName);
    });
    counts.chapters = names.length;
    counts.scenes = rows.filter((row) => row.kind === 'prose').length;
    counts.split = true;
  }
  return { counts, decisions };
}
